import React, { useState, useEffect, useRef } from 'react';
import Box from '@material-ui/core/Box';
import List from '@material-ui/core/List';
import ListItem from '@material-ui/core/ListItem';
import ListItemAvatar from '@material-ui/core/ListItemAvatar';
import ListItemText from '@material-ui/core/ListItemText';
import Avatar from '@material-ui/core/Avatar';
import Typography from '@material-ui/core/Typography';
import TextField from '@material-ui/core/TextField';
import IconButton from '@material-ui/core/IconButton';
import InputAdornment from '@material-ui/core/InputAdornment';
import Button from '@material-ui/core/Button';
import SendIcon from '@material-ui/icons/Send';
import AttachFileIcon from '@material-ui/icons/AttachFile';
import InsertDriveFileIcon from '@material-ui/icons/InsertDriveFile';
import CloseIcon from '@material-ui/icons/Close';
import SearchIcon from '@material-ui/icons/Search';

// ─── INSTANCIA REAL Y RUTA DE TU PROYECTO ───
import { firestore } from '../../../firebase/firebasejs';

// Estilos locales de la misma carpeta
import { useChatStyles } from './chats.styles';

export default function Chats() {
  const classes = useChatStyles();
  const inputFileRef = useRef(null);

  const [busqueda, setBusqueda] = useState("");
  const [chatActivoPhone, setChatActivoPhone] = useState(null);
  const [nuevoMensaje, setNuevoMensaje] = useState("");

  const [archivoPrevisualizado, setArchivoPrevisualizado] = useState(null);
  const [urlVistaPrevia, setUrlVistaPrevia] = useState("");

  const [contactos, setContactos] = useState([]);
  const [mensajes, setMensajes] = useState([]);

  const chatActivo = contactos.find(c => c.phone === chatActivoPhone);

  // 1️⃣ ESCUCHADOR EN TIEMPO REAL: CONTACTOS (SINTAXIS V8 COMPAT)
  useEffect(() => {
    const unsubscribe = firestore.collection("whatsappContacts")
      .orderBy("updatedAt", "desc")
      .onSnapshot((snapshot) => {
        const listaContactos = snapshot.docs.map(docSnap => ({
          id: docSnap.id,
          ...docSnap.data()
        }));
        setContactos(listaContactos);
      }, (error) => {
        console.error("Error al escuchar whatsappContacts: ", error);
      });

    return () => unsubscribe();
  }, []);

  // 2️⃣ ESCUCHADOR EN TIEMPO REAL: SUBCOLECCIÓN DE MENSAJES (SINTAXIS V8 COMPAT)
  useEffect(() => {
    if (!chatActivoPhone) {
      setMensajes([]);
      return;
    }

    const unsubscribe = firestore.collection("whatsappContacts")
      .doc(chatActivoPhone)
      .collection("messages")
      .orderBy("timestamp", "asc")
      .onSnapshot((snapshot) => {
        const listaMensajes = snapshot.docs.map(docSnap => ({
          messageId: docSnap.id,
          ...docSnap.data()
        }));
        setMensajes(listaMensajes);
      }, (error) => {
        console.error("Error al escuchar mensajes: ", error);
      });

    return () => unsubscribe();
  }, [chatActivoPhone]);

  const contactosFiltrados = contactos.filter(chat =>
    (chat.profileName && chat.profileName.toLowerCase().includes(busqueda.toLowerCase())) ||
    (chat.phone && chat.phone.includes(busqueda))
  );

  const formatearFecha = (firebaseTimestamp) => {
    if (!firebaseTimestamp) return "";
    let fecha;
    if (firebaseTimestamp.seconds) {
      fecha = new Date(firebaseTimestamp.seconds * 1000);
    } else if (typeof firebaseTimestamp === 'number') {
      fecha = new Date(firebaseTimestamp * 1000);
    } else {
      fecha = new Date(firebaseTimestamp);
    }
    return fecha.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  // 3️⃣ ESCRITURA: ENVIAR MENSAJE DE TEXTO (TEXTO PLANO STRING DIRECTO)
  const handleEnviarTexto = async (e) => {
    e.preventDefault();
    if (!nuevoMensaje.trim() || !chatActivoPhone) return;

    try {
      const ahoraUnix = Math.floor(Date.now() / 1000);
      const tiempoFirestore = new Date();

      // status:"pending" hace que sendWhatsappOutboundMessage (Cloud
      // Function disparada al crearse este doc) llame a la API real de
      // WhatsApp y actualice este mismo doc a "sent" o "failed".
      await firestore.collection("whatsappContacts")
        .doc(chatActivoPhone)
        .collection("messages")
        .add({
          contactId: chatActivo.contactId || chatActivoPhone,
          createdAt: tiempoFirestore,
          direction: "outbound",
          from: "dashboard_user",
          from_user_id: "dashboard_id",
          type: "text",
          text: nuevoMensaje, // Guardado como string plano
          timestamp: ahoraUnix,
          status: "pending"
        });

      await firestore.collection("whatsappContacts")
        .doc(chatActivoPhone)
        .update({
          lastMessage: nuevoMensaje,
          lastMessageAt: tiempoFirestore,
          updatedAt: tiempoFirestore
        });

      setNuevoMensaje("");
    } catch (error) {
      console.error("Error al enviar mensaje: ", error);
    }
  };

  const handleFileChange = (e) => {
    const archivo = e.target.files[0];
    if (!archivo) return;

    setArchivoPrevisualizado(archivo);

    if (archivo.type.startsWith("image/")) {
      const urlTemporal = URL.createObjectURL(archivo);
      setUrlVistaPrevia(urlTemporal);
    } else {
      setUrlVistaPrevia("");
    }

    e.target.value = null;
  };

  // 4️⃣ ESCRITURA: ENVIAR ARCHIVO O IMAGEN CONFIRMADA
  const handleConfirmarEnviarArchivo = async () => {
    if (!archivoPrevisualizado || !chatActivoPhone) return;

    try {
      const ahoraUnix = Math.floor(Date.now() / 1000);
      const esImagen = archivoPrevisualizado.type.startsWith("image/");
      const tiempoFirestore = new Date();

      await firestore.collection("whatsappContacts")
        .doc(chatActivoPhone)
        .collection("messages")
        .add({
          contactId: chatActivo.contactId || chatActivoPhone,
          createdAt: tiempoFirestore,
          direction: "outbound",
          from: "dashboard_user",
          from_user_id: "dashboard_id",
          type: esImagen ? "image" : "document",
          text: archivoPrevisualizado.name, // Nombre del archivo como string plano
          urlLocal: urlVistaPrevia,
          timestamp: ahoraUnix
        });

      await firestore.collection("whatsappContacts")
        .doc(chatActivoPhone)
        .update({
          lastMessage: archivoPrevisualizado.name,
          lastMessageAt: tiempoFirestore,
          updatedAt: tiempoFirestore
        });

      cancelarVistaPrevia();
    } catch (error) {
      console.error("Error al subir archivo: ", error);
    }
  };

  const cancelarVistaPrevia = () => {
    setArchivoPrevisualizado(null);
    setUrlVistaPrevia("");
  };

  const simularClickInputFile = () => {
    if (inputFileRef.current) inputFileRef.current.click();
  };

  return (
    <Box className={classes.whatsappContainer}>

      {/* ================= COLUMNA IZQUIERDA ================= */}
      <Box className={classes.sidebar}>
        <Box className={classes.sidebarHeader}>
          <Avatar style={{ backgroundColor: '#54656f', color: '#fff' }}>U</Avatar>
          <Typography variant="subtitle1" style={{ marginLeft: '12px', fontFamily: 'MavenProBold', fontWeight: 700 }}>
            Mis Conversaciones
          </Typography>
        </Box>

        <Box className={classes.searchContactBar}>
          <TextField
            fullWidth
            variant="outlined"
            size="small"
            placeholder="Buscar o empezar un nuevo chat"
            className={classes.searchField}
            value={busqueda}
            onChange={(e) => setBusqueda(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon style={{ color: '#667781', fontSize: '20px' }} />
                </InputAdornment>
              ),
            }}
          />
        </Box>

        <List className={classes.chatList} disablePadding>
          {contactosFiltrados.map((chat) => (
            <ListItem
              button
              key={chat.phone}
              className={`${classes.chatItem} ${chat.phone === chatActivoPhone ? classes.activeChatItem : ''}`}
              onClick={() => {
                setChatActivoPhone(chat.phone);
                cancelarVistaPrevia();
              }}
            >
              <ListItemAvatar style={{ marginRight: '8px' }}>
                <Avatar className={classes.sidebarAvatar}>
                  {chat.profileName ? chat.profileName.charAt(0).toUpperCase() : "?"}
                </Avatar>
              </ListItemAvatar>
              <ListItemText
                primary={
                  <Typography style={{ fontFamily: 'MavenProBold', fontWeight: 700, fontSize: '15px', color: '#111b21' }}>
                    {chat.profileName || "Sin Nombre"}
                  </Typography>
                }
                secondary={
                  <Box>
                    <Typography variant="body2" style={{ color: '#667781', fontSize: '13px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', marginTop: '2px' }}>
                      {chat.lastMessage}
                    </Typography>
                    <Typography className={classes.phoneSubtitle}>
                      +{chat.phone}
                    </Typography>
                  </Box>
                }
              />
              <Box alignSelf="flex-start" style={{ marginTop: '4px' }}>
                <Typography style={{ fontSize: '11px', color: '#667781', fontFamily: 'MavenProRegular' }}>
                  {formatearFecha(chat.lastMessageAt)}
                </Typography>
              </Box>
            </ListItem>
          ))}
        </List>
      </Box>

      {/* ================= COLUMNA DERECHA ================= */}
      <Box className={classes.chatWindow}>
        {chatActivo ? (
          <>
            {/* Pantalla de Confirmación de Archivo */}
            {archivoPrevisualizado && (
              <Box className={classes.previewOverlay}>
                <Box className={classes.previewHeader}>
                  <IconButton size="small" onClick={cancelarVistaPrevia} style={{ color: '#666', marginRight: '16px' }}>
                    <CloseIcon />
                  </IconButton>
                  <Typography style={{ fontFamily: 'MavenProBold', fontWeight: 700 }}>Enviar archivo</Typography>
                </Box>

                <Box className={classes.previewBody}>
                  {urlVistaPrevia ? (
                    <img src={urlVistaPrevia} alt="Preview" className={classes.previewImage} />
                  ) : (
                    <Box className={classes.previewFileBox}>
                      <InsertDriveFileIcon style={{ color: '#ff4400', fontSize: '64px' }} />
                      <Typography style={{ fontFamily: 'MavenProBold', fontWeight: 700, fontSize: '15px' }}>
                        {archivoPrevisualizado.name}
                      </Typography>
                      <Typography variant="caption" style={{ color: '#667781' }}>
                        {(archivoPrevisualizado.size / 1024).toFixed(1)} KB
                      </Typography>
                    </Box>
                  )}
                </Box>

                <Box className={classes.previewFooter}>
                  <Button onClick={cancelarVistaPrevia} style={{ color: '#666', fontFamily: 'MavenProRegular' }}>
                    Cancelar
                  </Button>
                  <Button
                    variant="contained"
                    className={classes.btnEnviarDocumento}
                    onClick={handleConfirmarEnviarArchivo}
                    startIcon={<SendIcon />}
                  >
                    Enviar documento
                  </Button>
                </Box>
              </Box>
            )}

            {/* Header del chat */}
            <Box className={classes.windowHeader}>
              <Box className={classes.headerInfo}>
                <Avatar style={{ backgroundColor: '#ff4400', color: '#ffffff', fontFamily: 'MavenProBold', fontWeight: 700 }}>
                  {chatActivo.profileName ? chatActivo.profileName.charAt(0).toUpperCase() : "?"}
                </Avatar>
                <Box>
                  <Typography style={{ fontFamily: 'MavenProBold', fontWeight: 700, fontSize: '15px', display: 'block', lineHeight: 1.2 }}>
                    {chatActivo.profileName}
                  </Typography>
                  <Typography variant="caption" style={{ color: '#667781', display: 'block', marginTop: '2px' }}>
                    +{chatActivo.phone} • Vía {chatActivo.source}
                  </Typography>
                </Box>
              </Box>

              <IconButton onClick={() => setChatActivoPhone(null)} title="Cerrar chat" size="small" style={{ color: '#666' }}>
                <CloseIcon />
              </IconButton>
            </Box>

            {/* Área de Mensajes */}
            <Box className={classes.messageArea}>
              {mensajes.map((msg) => {
                const esMensajeEnviado = msg.direction === "outbound";

                return (
                  <Box
                    key={msg.messageId}
                    className={`${classes.messageBubble} ${esMensajeEnviado ? classes.sentMessage : classes.receivedMessage}`}
                  >
                    {msg.mediaError ? (
                      <Typography style={{ fontSize: '13px', color: '#d32f2f' }}>
                        No se pudo descargar el adjunto: {msg.mediaError}
                      </Typography>
                    ) : msg.type === "image" ? (
                      <img src={msg.urlLocal} alt="Adjunto" style={{ maxWidth: '100%', borderRadius: '4px', display: 'block', marginBottom: '4px' }} />
                    ) : msg.type === "document" || msg.type === "file" ? (
                      <Box
                        className={classes.fileBubbleContent}
                        component={msg.urlLocal ? "a" : "div"}
                        href={msg.urlLocal || undefined}
                        target={msg.urlLocal ? "_blank" : undefined}
                        rel={msg.urlLocal ? "noopener noreferrer" : undefined}
                        style={{ textDecoration: 'none', color: 'inherit' }}
                      >
                        <InsertDriveFileIcon style={{ color: '#ff4400' }} />
                        <Typography className={classes.fileNameText} title={msg.text}>
                          {msg.text}
                        </Typography>
                      </Box>
                    ) : (
                      /* Renderizado directo del String desde Firestore */
                      <Typography style={{ fontSize: '14px' }}>{msg.text}</Typography>
                    )}
                    <Typography className={classes.messageTime}>
                      {formatearFecha(msg.timestamp || msg.createdAt)}
                    </Typography>
                    {esMensajeEnviado && msg.status === "pending" && (
                      <Typography style={{ fontSize: '11px', color: '#667781', marginTop: '2px' }}>
                        Enviando...
                      </Typography>
                    )}
                    {esMensajeEnviado && msg.status === "sending" && (
                      <Typography style={{ fontSize: '11px', color: '#667781', marginTop: '2px' }}>
                        Enviando...
                      </Typography>
                    )}
                    {esMensajeEnviado && msg.status === "failed" && (
                      <Typography style={{ fontSize: '11px', color: '#d32f2f', marginTop: '2px' }}>
                        No se pudo enviar: {msg.error || "error desconocido"}
                      </Typography>
                    )}
                  </Box>
                );
              })}
            </Box>

            {/* Input inferior */}
            <Box component="form" onSubmit={handleEnviarTexto} className={classes.inputArea}>
              <input type="file" className={classes.hiddenInputFile} ref={inputFileRef} onChange={handleFileChange} />

              <IconButton size="small" onClick={simularClickInputFile} title="Adjuntar documento o archivo">
                <AttachFileIcon style={{ transform: 'rotate(45deg)' }} />
              </IconButton>

              <TextField
                fullWidth
                variant="outlined"
                size="small"
                placeholder="Escribe un mensaje aquí"
                className={classes.inputField}
                value={nuevoMensaje}
                onChange={(e) => setNuevoMensaje(e.target.value)}
              />

              <IconButton type="submit" size="small" style={{ color: nuevoMensaje.trim() ? '#ff4400' : '#666' }}>
                <SendIcon />
              </IconButton>
            </Box>
          </>
        ) : (
          <Box className={classes.emptyStateContainer}>
            <Typography style={{ color: '#667781', fontFamily: 'MavenProRegular', fontSize: '15px', fontWeight: 500 }}>
              Selecciona un chat de la lista para leer y responder mensajes
            </Typography>
          </Box>
        )}
      </Box>

    </Box>
  );
}